import axios from "axios";
import store from "@/store";
import { message } from "ant-design-vue";
import { geoBlockedNotice, mfaCheckUnavailableNotice, isLoginRequest, retryAfterStepUp, withMfaHeader } from "@/utils/mfa";
import { requestStepUp } from "@/utils/stepUp";
import { reportGeoBlock } from "@/utils/geoBlockReport";
import { withClientHeaders } from "@/utils/clientHeaders";
import { needsCredentials } from "@/utils/consoleRefresh";
import { decodeBlobError, handleStandardError } from "@/utils/standardErrorHandler";


const apiInstance = axios.create({
  baseURL: process.env.VUE_APP_SERVERURL,
});

apiInstance.interceptors.request.use(
  (config) => {
    // 글로벌 로딩은 config에서 명시적으로 요청한 경우에만 활성화
    if (config.showGlobalLoading !== false) {
      store.commit("setIsLoading", true);
    }
    // config.headers["x-cert-from"] = process.env.VUE_APP_SALT;
    withClientHeaders(config);
    // 로그인·2FA 확인 응답이 심는 refresh 쿠키를 받으려면 쿠키를 주고받아야 한다(K-19, CI-07)
    if (needsCredentials(config.url)) config.withCredentials = true;

    // doctor-palette API는 x-api-key 인증 사용 (JWT 토큰 제외)
    const isDoctorPaletteApi = config.url && config.url.includes('/doctor-palette/');

    if (!isDoctorPaletteApi && localStorage.getItem("accessTokenCerti")) {
      config.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
      config.headers["Access-Control-Allow-Origin"] = "*";
      withMfaHeader(config);
    }
    return config;
  },
  (error) => {
    store.commit("setIsLoading", false);
    return Promise.reject(error);
}
);

let isTokenRefreshing = false;
let failedRequestsQueue = [];

apiInstance.interceptors.response.use(
    (response) => {
      // 글로벌 로딩을 켠 경우에만 끄기
      if (response.config.showGlobalLoading !== false) {
        store.commit("setIsLoading", false);
      }
      return response;
    },
    
async (error) => {
    // 글로벌 로딩을 켠 경우에만 끄기
    if (error.config && error.config.showGlobalLoading !== false) {
      store.commit("setIsLoading", false);
    }
    const originalRequest = error.config;
    await decodeBlobError(error);

    // doctor-palette API는 x-api-key 인증 사용하므로 JWT 토큰 갱신 로직 건너뛰기
    const isDoctorPaletteApi = originalRequest.url && originalRequest.url.includes('/doctor-palette/');
    if (isDoctorPaletteApi) {
      return Promise.reject(error);
    }

    // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 문구를 한 번(같은 key 라 겹치지 않음) 띄우고 그대로 실패시킨다(step-up 없음, AC-GEO-010c).
    // 요청이 showErrorNotice: false 면 부르는 쪽이 화면 안에 직접 보이므로 알림은 띄우지 않는다(같은 문구 두 번 방지)
    const notify = !(originalRequest && originalRequest.showErrorNotice === false);
    const geoNotice = geoBlockedNotice(error);
    if (geoNotice) {
      reportGeoBlock(isLoginRequest(originalRequest?.url) ? "LOGIN" : "REQUEST", originalRequest);
      if (notify) message.warning({ content: geoNotice, key: "GEO_BLOCKED" });
      return Promise.reject(error);
    }

    // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번(같은 key) 뒤 실패 — 재전송·step-up 없음(F-9)
    const unavailable = mfaCheckUnavailableNotice(error);
    if (unavailable) {
      if (notify) message.error({ content: unavailable, key: "MFA_CHECK_UNAVAILABLE" });
      return Promise.reject(error);
    }

    // 2차 인증 필요(MFA_REQUIRED·SEND_AUTH_REQUIRED) → step-up 창 → 통과하면 원래 요청 한 번 재전송
    const stepUp = retryAfterStepUp(error, { requestStepUp, resend: apiInstance });
    if (stepUp) return stepUp;

    // 새 계약 오류 코드(ACCOUNT_INACTIVE·PERMISSION_DENIED·AUDIT_UNAVAILABLE·OPERATOR_ACTING_DENIED 등): 알림 한 번 뒤 실패 — 토큰 갱신하지 않음(W6 E2)
    if (await handleStandardError(error, { notify })) return Promise.reject(error);

    // 로그인 단계의 401(LOGIN_FAILED 등)은 토큰 만료가 아니므로 갱신·재전송하지 않는다
    if (error.response && error.response.status === 401 && !originalRequest._retry && !isLoginRequest(originalRequest.url)) {
        if (!isTokenRefreshing) {
          
        isTokenRefreshing = true;
        await store.dispatch("requestAccessToken");
        isTokenRefreshing = false;

        failedRequestsQueue.forEach((queueItem) => {
          queueItem.resolve(apiInstance(queueItem.originalRequest));
        });
        failedRequestsQueue = [];

        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
        return apiInstance(originalRequest);
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve,
            reject,
            originalRequest,
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

export default apiInstance;